import { t, translate } from '@avalara/bat-apps-shared/i18n';
import { SCol, SContainer, SRow } from '@avalara/skylab-sdk/react';

import { Fragment, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { getConnectors, getLoginFromUrl, getMarketingDataFromUrl } from '../../lib/side-effects';
import { updateProductAnalytics } from '../../lib/side-effects/analytics';
import { getRiskAssessmentSalestax } from '../../lib/side-effects/avatax';
import { validateDiscountCode } from '../../lib/side-effects/offers';
import { selectState } from '../../selectors';
import { setDiscountCode } from '../../store/order';
import { setActiveFlow } from '../../store/system';
import ExternalLink from '../external-link';
import AccordionRoot from '../shared/accordion-root';
import { useAbandonedCart } from '../shared/hooks';

import { useAppDispatch } from '../../hooks/use-app-dispatch';
import { StyledContainer } from '../styled/container';
import AccountAndPaymentAccordions from './account-payment-accordions';
import CartInfo from './cart-info';
import { accordionContent } from './constants';
import RiskAssessmentBanner from './risk-banner';

const RiskAssessment = () => {
  const {
    account: { isLoggedIn },
    order: { discountPercent },
  } = useSelector(selectState);
  const dispatch = useAppDispatch();

  const searchParams = new URLSearchParams(window.location.search);
  // NOTE(Allie): Using 'c' for 'code' aka discount code so it isn't obvious to users
  const [discountCode] = useState(searchParams.get('c'));

  const [createAccountOpen, setCreateAccountOpen] = useState(true);
  const [sentPurchaseInfo, setSentPurchaseInfo] = useState(false);
  const [showConnectors, setShowConnectors] = useState(false);

  // Note(Andrew) - We need this to happen before the other useEffect's, since otherwise
  //                the auth callback results in a 500 from the aui-identity module
  //                (not sure why currently)
  useState(getLoginFromUrl());
  useAbandonedCart();

  const onAccountOrPaymentAccordionToggle = () => {
    if (createAccountOpen && !isLoggedIn) {
      dispatch(getRiskAssessmentSalestax());
    }
    if (!sentPurchaseInfo) {
      dispatch(updateProductAnalytics('purchase_start'));
      setSentPurchaseInfo(true);
    }
  };

  const riskAssessmentFlow = isLoggedIn ? 'risk-assessment' : 'risk-assessment-net-new';

  // biome-ignore lint/correctness/useExhaustiveDependencies: leaving as is
  useEffect(() => {
    dispatch(setActiveFlow(riskAssessmentFlow));

    if (discountCode != null) {
      dispatch(setDiscountCode(discountCode));
      dispatch(validateDiscountCode(riskAssessmentFlow, discountCode));
    }

    if (!isLoggedIn && discountPercent === 100) {
      setShowConnectors(true);
      dispatch(getConnectors());
    }

    if (isLoggedIn) {
      setCreateAccountOpen(false);
      setShowConnectors(false);
    }
  }, [isLoggedIn, discountPercent]);

  useEffect(() => {
    dispatch(getMarketingDataFromUrl());
  }, []);

  const GUIDED_QUESTIONNAIRE_ACCORDION_ITEM_INDEX = 0;

  return (
    <Fragment>
      <RiskAssessmentBanner />
      <StyledContainer>
        <SContainer className='justify-content-center'>
          <SRow className='pad-all-sm'>
            <SCol center='' span='sm-10 md-7 lg-5 5' className='margin-bottom-xl'>
              <div className='left-col-header font-light'>{t('client.general.what-to-expect')}</div>
              <AccordionRoot
                contents={accordionContent}
                defaultOpenItem={GUIDED_QUESTIONNAIRE_ACCORDION_ITEM_INDEX}
                flow={riskAssessmentFlow}
              />
              <h3 className='alt pad-bottom-sm pad-top-md'>{t('client.headings.questions')}</h3>
              {discountCode && (
                <p>
                  {translate('client.general.learn-more-about-the-sales-tax-risk-assessment', {
                    learnMoreLink: (chunks) => (
                      <ExternalLink
                        alwaysOpenInNewTab
                        href='https://www.avalara.com/us/en/products/professional-services/sales-tax-risk-assessment.html'
                      >
                        {chunks}
                      </ExternalLink>
                    ),
                  })}
                </p>
              )}
              <p>
                {discountCode ? 'Or call ' : ''}
                <a href='tel://+18772243650'>1 (877) 224-3650</a>
              </p>
            </SCol>
            <SCol center='' span='sm-10 md-7 lg-6 5' offset='xl-1 lg-1' className='pad-left-none pad-right-none'>
              <CartInfo />
              <h1 className='pad-top-md pad-bottom-sm'>{t('client.headings.start-your-sales-tax-risk-assessment')}</h1>
              <AccountAndPaymentAccordions
                createAccountOpen={createAccountOpen}
                isLoggedIn={isLoggedIn}
                onAccountOrPaymentAccordionToggle={onAccountOrPaymentAccordionToggle}
                paymentView='risk-assessment'
                accountTitleText='Enter your contact info'
                hidePaymentPage={discountCode != null && discountPercent === 100}
                showConnectors={showConnectors}
              />
            </SCol>
          </SRow>
        </SContainer>
      </StyledContainer>
    </Fragment>
  );
};

export default RiskAssessment;
